import { createRoot } from "react-dom/client";
import { App, AppInputRawData, parseAppInputData } from "app";
import { appStore } from "entities/App";
import { invoker } from "shared/lib/callbacks.lib";
// import { MicrofrontendInstanceManager } from "shared/lib/mf.lib";
import { parseJSONLikeString } from "shared/lib/parseJSONLikeString.lib";
import { store } from "shared/lib/redux";
import { AppDOM, createInitialNodes, googleRecaptcha, InitialNodesReturnType } from "shared/utils/app.utils";
import { isAppIntegration } from "shared/utils/url.utils";

const createRootWithShadowDom = (
  { appContainer, appStylesheetNode }: InitialNodesReturnType,
  rootElement: HTMLElement,
  mode: ShadowRootInit["mode"] = "open"
) => {
  const shadowRootNode = rootElement.attachShadow({ mode });
  AppDOM.setShadowRootNode(shadowRootNode);

  // always attach app stylesheet link so styles are available in dev remote integration
  appContainer.appendChild(appStylesheetNode);

  shadowRootNode.appendChild(appContainer);
};
const createRootWithoutShadowDom = (
  { appContainer, appRoot, appStylesheetNode }: InitialNodesReturnType,
  rootElement: HTMLElement
) => {
  // always attach app stylesheet link so styles are available in dev remote integration
  appContainer.appendChild(appStylesheetNode);

  appContainer.appendChild(appRoot);
  rootElement.appendChild(appContainer);
};

const main = () => {
  const rootNode: HTMLElement | null = document.getElementById("total_tracking_root");
  if (!rootNode) return;

  const appInputRawData = parseJSONLikeString<AppInputRawData>(rootNode.dataset.filter);
  const appInputParsedData = parseAppInputData(appInputRawData);

  // Initialize urlBehaviour in Redux BEFORE rendering the app
  // This ensures QueryParamsManager is created with the correct behaviour
  store.dispatch(appStore.actions.setUrlBehaviour(appInputParsedData.urlBehaviour));

  // delete all children from rootNode if shadowDom is false and app is not integrated
  if (!appInputParsedData.shadowDom && !isAppIntegration) {
    while (rootNode.firstChild) {
      rootNode.removeChild(rootNode.firstChild);
    }
  }

  const initialNodes = createInitialNodes();
  const { appContainer, appRoot, appStylesheetNode, fontsRegisterStylesheetNode, fontsStylesheetNode, modalNode } =
    initialNodes;

  let googleRecaptchaScript: HTMLScriptElement | null = null;
  if (isAppIntegration) {
    googleRecaptchaScript = googleRecaptcha.createScriptElement();
    rootNode.appendChild(googleRecaptchaScript);
  }

  rootNode.appendChild(fontsRegisterStylesheetNode);
  appContainer.appendChild(fontsStylesheetNode);
  AppDOM.setAppContainer(appContainer);
  appContainer.appendChild(appRoot);
  appContainer.appendChild(modalNode);

  if (isAppIntegration || appInputParsedData.shadowDom) {
    createRootWithShadowDom(initialNodes, rootNode);
  } else {
    createRootWithoutShadowDom(initialNodes, rootNode);
  }

  if (appInputRawData?.height) {
    appContainer.style.height = appInputRawData.height;
    appContainer.style.maxHeight = appInputRawData.height;
    appContainer.style.minHeight = "0";
  }

  const renderApp = () => {
    createRoot(appRoot).render(<App inputData={appInputParsedData} />);
  };

  const renderAppDependOnRecaptcha = () => {
    if (!googleRecaptchaScript) {
      renderApp();
      return;
    }

    let appRendered = false;

    googleRecaptchaScript.addEventListener("load", () => {
      googleRecaptcha.ready(() => {
        if (appRendered) return;
        appRendered = true;
        renderApp();
      });
    });

    if (!appRendered) {
      // invoker trying to resolve with delay in 300ms if app rendered after recaptcha
      // if not resolved after 4 retries, app will render without recaptcha as fallback
      invoker(
        ({ isInvokeLimitReached }) => {
          if (isInvokeLimitReached && !appRendered) {
            appRendered = true;
            renderApp();
          }
        },
        {
          invokeLimit: 5,
          frequencyInMilliseconds: 300,
          immediateFirstInvoke: false,
          shouldInvokeResolver: () => !appRendered,
        }
      );

      googleRecaptchaScript.addEventListener("error", () => {
        // Fallback to render app without recaptcha
        renderApp();
      });
    }
  };

  // Microfrontend disabled — no @qubictron/microfrontend package.
  // MicrofrontendInstanceManager.createInstance({
  //   appContainer,
  //   shouldAddStylesheets: appInputParsedData.shadowDom || isAppIntegration,
  // });

  if (__DEV_SERVER_LAUNCHED__) {
    renderApp();
  } else {
    appStylesheetNode.addEventListener("load", () => {
      renderAppDependOnRecaptcha();
    });
  }
};

if (__DEV_SERVER_LAUNCHED__) {
  main();
} else {
  (function checkForInit() {
    if (document.readyState === "complete") {
      return main();
    }
    window.requestAnimationFrame(() => {
      checkForInit();
    });
  })();
}
